import React, { useRef, useState } from 'react';
import { IngredientsTable } from 'components/ingredients/Index';
import Header from 'components/header/Header';
import { IStyle } from 'Styles';
import useIngredientHeaderButtons from 'components/ingredients/hooks/useIngredientHeaderButtons';
import { IngredientsImportManager } from 'components/main/Setup/Ingredients/conflictModal/IngredientsImportManager';
import AddIngredientTypes from 'components/ingredients/AddIngredientType';
import LiquidClassModal from 'components/ingredients/LiquidClassModal';
import VendorModal from 'components/ingredients/VendorModal';
import useIngredientsSelectedRows from 'components/ingredients/hooks/useIngredientsSelectedRowsList';
import {
  getIngredientsByIds,
  printIngredientBarcode,
  ingredientsExportToExcel,
  ingredientsExportToText,
  ingredientsExportToXML,
  ingredientsExportToXMLSpreadsheet,
  deleteIngredients,
} from 'business/services/IngredientService';
import { showAlertSnackBar, downloadFileSaveAs } from 'utils/Global';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import {
  getTypedIngredientByIngredientId,
  isBufferStockConsistentAmongIngredients,
  useExportButtonsList,
} from 'components/ingredients/IngredientUtils';
import { MergeIngredients } from 'components/ingredients/mergeIngredients/MergeIngredients';
import { useRMSelector, useRMDispatch } from 'redux/hooks';
import {
  ExtendedIngredientDTO,
  PrintBarcodeApiResponse,
  FileResponse,
  IngredientTableRow,
} from 'business/models/Models';
import { GetExportButtonsListParams } from 'components/ingredients/IngredientConstants';
import useIngredientsList from 'components/ingredients/hooks/useIngredientsList';
import {
  setIngTableDataIndicator,
  setIngredientSelectedRows,
} from 'redux/reducers/ingredientTableReducer';
import RMModal from 'components/modal/RMModal';
import { ModalDetails } from 'business/models/CustomModels';
import DetailView from './DetailView';
import { LoadingModal } from 'components/modal';
import AddProteinType from 'components/ingredients/AddProteinType';
import { useEventListener } from 'CustomHooks/useEventListener';
import { getNormalizedKeyboardKey } from 'keyboardShortcuts/utils';
import { KeyboardKeys } from 'constants/types';
import { useContainerFocus } from 'CustomHooks/useContainerFocus';
import { useShowLabelPrinterError } from 'CustomHooks';

declare global {
  interface Window {
    showSaveFilePicker: any;
    showOpenFilePicker: any;
  }
}

type IngredientModalProps = {
  show: boolean;
  id: number;
};

const Ingredients = () => {
  const [showIngredientTypeModal, setShowIngredientTypeModal] = useState<boolean>(false);
  const [showProteinTypeModal, setShowProteinTypeModal] = useState<boolean>(false);
  const [showLiquidClassModal, setShowLiquidClassModal] = useState<boolean>(false);
  const [showVendorModal, setShowVendorModal] = useState<boolean>(false);
  const [ingredientModalProps, setIngredientModalProps] = useState<IngredientModalProps>({
    show: false,
    id: -1,
  });
  const ingredientsSelectedRows = useIngredientsSelectedRows();
  const ingredientData: IngredientTableRow[] = useIngredientsList();
  const modelRef = useRef(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [ingredients, setIngredients] = useState<ExtendedIngredientDTO[]>([]);
  const selectedRows = useRMSelector((state) => state.ingredientsTable.selectedRows);
  const ingTableDataIndicator = useRMSelector(
    (state) => state.ingredientsTable.ingTableDataIndicator,
  );
  const dispatch = useRMDispatch();
  const [modalDetails, setModalDetails] = useState<ModalDetails>({
    showModal: false,
    setShowModal: () => {},
    onOk: () => {},
    body: null,
    heading: '',
    isOkOnly: false,
    okButtonText: undefined,
    noButtonText: undefined,
    showButtons: true,
    dialogClassName: '',
    isCancelOnly: false,
  });
  const [showLoadinModal, setShowLoadingModal] = useState<boolean>(false);
  const [filteredIngredientRowsIndex, setFilteredIngredientRowsIndex] = useState([]);
  const showLabelPrinterError = useShowLabelPrinterError();

  const modelRefCurrent = () => {
    modelRef.current();
  };

  const addIngredientType = () => {
    setShowIngredientTypeModal(true);
  };

  const addProteinType = () => {
    setShowProteinTypeModal(true);
  };

  const addVendor = () => {
    setShowVendorModal(true);
  };

  const addLiquidClass = () => {
    setShowLiquidClassModal(true);
  };

  const showDeleteIngredientWarning = async () => {
    setModalDetails({
      ...modalDetails,
      showModal: true,
      body: 'Are you sure you want to delete the selected ingredients?',
      heading: 'Warning',
      okButtonText: 'Yes',
      noButtonText: 'No',
      onOk: deleteSelectedIngredients,
      dialogClassName: '',
      isOkOnly: false,
      isCancelOnly: false,
    });
  };

  const deleteSelectedIngredients = async () => {
    setModalDetails({ ...modalDetails, showModal: false });
    setShowLoadingModal(true);

    const selectedIngredientsIds = [];
    for (const ingRow of ingredientsSelectedRows) {
      if (!selectedIngredientsIds.some((id) => id == ingRow.id)) {
        selectedIngredientsIds.push(ingRow.id);
      }
    }
    const response = await deleteIngredients(selectedIngredientsIds);
    if (!response.allIngsDeleted) {
      if (!response.isIngredientTableUpdateRequired) {
        setShowLoadingModal(false);
      }
      showAlertSnackBar(
        EAlertType.WARNING,
        ESnackBarPosition.TOP,
        'Some of the ingredients are in use and can not be deleted.',
      );
    }
    if (response.isIngredientTableUpdateRequired) {
      dispatch(setIngredientSelectedRows([]));
      dispatch(setIngTableDataIndicator(ingTableDataIndicator + 1));
    }
  };

  const exportIngredientsToExcel = async () => {
    const fileResponse = await ingredientsExportToExcel(
      ingredientData.filterByIndexes(filteredIngredientRowsIndex),
    );
    await downloadFileSaveAs(fileResponse, '.xls', 'Ingredients');
  };

  const exportIngredientsToText = async () => {
    const fileResponse = await ingredientsExportToText(
      ingredientData.filterByIndexes(filteredIngredientRowsIndex),
    );
    await downloadFileSaveAs(fileResponse, '.txt', 'Ingredients');
  };

  const exportIngredientsToXML = async () => {
    const fileResponse = await ingredientsExportToXML(ingredientsSelectedRows);
    await downloadFileSaveAs(fileResponse, '.xml', 'Ingredients');
  };

  const exportIngredientsToXMLSpreadsheet = async () => {
    const fileResponse = await ingredientsExportToXMLSpreadsheet(
      ingredientData.filterByIndexes(filteredIngredientRowsIndex),
    );
    await downloadFileSaveAs(fileResponse, '.xml', 'Ingredients');
  };

  const exportIngredientsToDoc = async () => {
    const tab = '</td><td>';
    let strData: string =
      '<tr><td><b>ID</b></td>' +
      '<td><b>Type</b></td>' +
      '<td><b>Name</b></td>' +
      '<td><b>Short Name</b></td>' +
      '<td><b>Default Stock Number</b></td>' +
      '<td><b>Conc. Units</b></td>' +
      '<td><b>Stock Conc.</b></td>' +
      '<td><b>Low Conc.</b></td>' +
      '<td><b>High Conc.</b></td>' +
      '<td><b>Liquid Class</b></td>' +
      '<td><b>Stock Min pH</b></td>' +
      '<td><b>Stock Max pH</b></td></tr>';

    ingredientData.filterByIndexes(filteredIngredientRowsIndex).forEach((ingredient) => {
      strData += `<tr><td>${ingredient.id}${tab}${ingredient.type}${tab}${
        ingredient.name
      }${tab}${ingredient.shortName}${tab}${ingredient.defaultStockNumber}${tab}${
        ingredient.concUnit
      }${tab}${ingredient.stockConc}${tab}${ingredient.lowConc}${tab}${
        ingredient.highConc
      }${tab}${ingredient.liquidClassName}${tab}${
        ingredient.stockMinPh == null ? '' : ingredient.stockMinPh
      }${tab}${ingredient.stockMaxPh == null ? '' : ingredient.stockMaxPh}</td></tr>\n`;
    });

    const docString = `<table border="1" cellpadding="3" cellspacing="3">${strData}</table>`;
    const fileResponse: FileResponse = {} as FileResponse;
    fileResponse.data = new Blob([docString]);
    await downloadFileSaveAs(fileResponse, '.doc', 'Ingredients');
  };

  const printLabels = async () => {
    if (ingredientsSelectedRows.length > 0) {
      let printResponse: PrintBarcodeApiResponse;
      for (const ing of ingredientsSelectedRows) {
        printResponse = await printIngredientBarcode(ing.stockId);
        if (printResponse.value != '') {
          break;
        }
      }
      if (printResponse.value != '') {
        showLabelPrinterError();
      } else {
        showAlertSnackBar(
          EAlertType.SUCCESS,
          ESnackBarPosition.TOP,
          'The ingredients labels have been printed successfully!',
        );
      }
    } else {
      showAlertSnackBar(
        EAlertType.WARNING,
        ESnackBarPosition.TOP,
        'No ingredient is selected.',
      );
    }
  };

  const onClickMergeIngredient = async () => {
    if (selectedRows.find((ing) => ing.name.toLowerCase() === 'water')) {
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        'You cannot merge water.',
      );
      return;
    }
    const typedIngredient = getTypedIngredientByIngredientId(
      ingredientData,
      selectedRows.map((row) => {
        return row.id;
      }),
    );
    if (isBufferStockConsistentAmongIngredients(typedIngredient)) {
      const tempIngredients = await getIngredientsByIds(
        selectedRows.map((row) => {
          return row.id;
        }),
      );
      setIngredients(tempIngredients);
      setShowModal(true);
    } else {
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        'You cannot merge a buffer with a non-buffer.',
      );
    }
  };

  const openIngredient = () => {
    if (selectedRows?.length === 1) {
      setIngredientModalProps({ show: true, id: selectedRows[0].id });
    }
  };

  const createIngredient = () => {
    setIngredientModalProps({ show: true, id: -1 });
  };

  const ingredientExportListParams: GetExportButtonsListParams = {
    exportIngredientsToExcel,
    exportIngredientsToText,
    exportIngredientsToDoc,
    exportIngredientsToXML,
    exportIngredientsToXMLSpreadsheet,
  };
  const exportsList = useExportButtonsList(ingredientExportListParams);
  const { ingredientHeaderButtons } = useIngredientHeaderButtons({
    modelRefCurrent,
    addIngredientType,
    addVendor,
    addLiquidClass,
    printLabels,
    exportsList,
    onClickMergeIngredient,
    showDeleteIngredientWarning,
    openIngredient,
    createIngredient,
    addProteinType,
  });
  const containerRef = useRef(null);
  const [isFocused] = useContainerFocus(containerRef, true);

  useEventListener('keyup', (e) => {
    if (!isFocused) {
      return;
    }
    const key = getNormalizedKeyboardKey(e);
    if (key === KeyboardKeys.Delete && ingredientsSelectedRows.length > 0) {
      showDeleteIngredientWarning();
    }
  });
  return (
    <>
      <LoadingModal showModal={showLoadinModal} text={'Loading...'} />
      <RMModal
        id="ingredients-modal"
        showModal={modalDetails.showModal}
        setShowModal={() => setModalDetails({ ...modalDetails, showModal: false })}
        onOk={modalDetails.onOk}
        body={modalDetails.body}
        heading={modalDetails.heading}
        isOkOnly={modalDetails.isOkOnly}
        okButtonText={modalDetails.okButtonText}
        dialogClassName={modalDetails.dialogClassName}
        isCancelOnly={modalDetails.isCancelOnly}
      />
      <MergeIngredients
        showModal={showModal}
        setShowModal={setShowModal}
        ingredients={ingredients}
        setIngredients={setIngredients}
      ></MergeIngredients>
      <IngredientsImportManager modelRef={modelRef} isScreen={false} />
      <AddIngredientTypes
        showIngredientTypeModal={showIngredientTypeModal}
        setShowIngredientTypeModal={setShowIngredientTypeModal}
      />
      <AddProteinType
        showProteinTypeModal={showProteinTypeModal}
        setShowProteinTypeModal={setShowProteinTypeModal}
      />
      <VendorModal
        showVendorModal={showVendorModal}
        setShowVendorModal={setShowVendorModal}
      />
      <LiquidClassModal
        showLiquidClassModal={showLiquidClassModal}
        setShowLiquidClassModal={setShowLiquidClassModal}
      />

      <DetailView
        ingredientId={ingredientModalProps.id}
        showModal={ingredientModalProps.show}
        setShowModal={(e) =>
          setIngredientModalProps({ ...ingredientModalProps, show: e })
        }
      />
      <div id="ingredients-container" className="custom-tabs">
        <div id="ingredients-content-wrapper" className="setup-bg-topbar">
          <Header
            containerStyle={styles.header}
            headerButtons={ingredientHeaderButtons}
            isNewTheme={true}
            title={'Ingredients'}
          />
          <div
            id="ingredients-content-table"
            className="mainContainer bg-white"
            ref={containerRef}
            style={styles.contentContainer}
          >
            <IngredientsTable
              setShowLoadingModal={setShowLoadingModal}
              setFilteredIngredientRowsIndex={setFilteredIngredientRowsIndex}
            />
          </div>
        </div>
      </div>
    </>
  );
};

const styles: IStyle = {
  root: {
    width: '100%',
    padding: 10,
    display: 'flex',
    gap: 10,
    flexDirection: 'column',
  },
  header: {
    marginTop: -6,
  },
  contentContainer: {
    height: '600px',
    overflow: 'auto',
  },
};

export default Ingredients;
